import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { MapPin, CheckCircle2 } from "lucide-react";
import loadGoogleMaps from "@/lib/loadGoogleMaps";

const DEBOUNCE_MS = 250;

/**
 * Verified address widget — plain styled <Input> (matches the rest of the
 * app exactly) with a custom-styled suggestions dropdown fed by Google's
 * data-only Autocomplete Suggestions API. Deliberately doesn't use
 * Google's own PlaceAutocompleteElement widget, which can't be restyled
 * to match this app's design system beyond a handful of CSS variables.
 *
 * Props:
 *   value           : { address_line, address_place_id?, address_lat?, address_lng?, address_components? }
 *   onChange(value) : called with a merged value on every edit
 *   testid          : test id root
 */
export default function AddressInput({
    value = {},
    onChange,
    testid = "address-input",
    disabled = false,
    required = false,
    placeholder = "Start typing your address…",
}) {
    const [line, setLine] = useState(value.address_line || "");
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);

    const googleRef = useRef(null);
    const sessionTokenRef = useRef(null);
    const debounceRef = useRef(null);
    const wrapRef = useRef(null);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const valueRef = useRef(value);
    valueRef.current = value;

    const isVerified = Boolean(value.address_place_id || value.address_lat);

    const emit = (patch) => {
        onChangeRef.current?.({
            ...valueRef.current,
            ...patch,
        });
    };

    useEffect(() => {
        let cancelled = false;
        loadGoogleMaps()
            .then((google) => {
                if (cancelled) return;
                googleRef.current = google;
            })
            .catch((err) => {
                // No key configured, script blocked, or API error — the
                // field just stays plain manual entry, no dropdown ever
                // appears. Logged so a misconfigured API/key is diagnosable.
                console.error("Google Places autocomplete unavailable:", err);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    // Close the dropdown on outside click.
    useEffect(() => {
        const onDocClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    const ensureSessionToken = () => {
        const google = googleRef.current;
        if (!google) return null;
        if (!sessionTokenRef.current) {
            sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
        }
        return sessionTokenRef.current;
    };

    const fetchSuggestions = async (input) => {
        const google = googleRef.current;
        if (!google || !input.trim()) {
            setSuggestions([]);
            setOpen(false);
            return;
        }
        try {
            const { suggestions: results } =
                await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
                    input,
                    sessionToken: ensureSessionToken(),
                });
            setSuggestions(results || []);
            setOpen((results || []).length > 0);
            setActiveIndex(-1);
        } catch (err) {
            console.error("Address suggestion fetch failed:", err);
            setSuggestions([]);
            setOpen(false);
        }
    };

    const handleInputChange = (e) => {
        const v = e.target.value;
        setLine(v);
        emit({
            address_line: v,
            address_place_id: null,
            address_lat: null,
            address_lng: null,
        });
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => fetchSuggestions(v), DEBOUNCE_MS);
    };

    const selectSuggestion = async (suggestion) => {
        const placePrediction = suggestion.placePrediction;
        const place = placePrediction.toPlace();
        await place.fetchFields({
            fields: ["formattedAddress", "location", "addressComponents", "id"],
        });
        const formatted = place.formattedAddress || placePrediction.text?.toString() || "";
        setLine(formatted);
        setOpen(false);
        setSuggestions([]);
        emit({
            address_line: formatted,
            address_place_id: place.id,
            address_lat: place.location?.lat?.() ?? null,
            address_lng: place.location?.lng?.() ?? null,
            address_components: place.addressComponents || null,
        });
        // Selection ends the billed session — next search starts a fresh one.
        sessionTokenRef.current = null;
    };

    const handleKeyDown = (e) => {
        if (!open || suggestions.length === 0) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex((i) => (i + 1) % suggestions.length);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
        } else if (e.key === "Enter") {
            if (activeIndex >= 0) {
                e.preventDefault();
                selectSuggestion(suggestions[activeIndex]);
            }
        } else if (e.key === "Escape") {
            setOpen(false);
        }
    };

    return (
        <div data-testid={testid} className="space-y-1.5" ref={wrapRef}>
            <div className="relative">
                <MapPin
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-navy-400 pointer-events-none"
                />
                <Input
                    data-testid={`${testid}-line`}
                    disabled={disabled}
                    required={required}
                    value={line}
                    placeholder={placeholder}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => suggestions.length > 0 && setOpen(true)}
                    autoComplete="off"
                    className="pl-8 h-11 bg-white border-navy-100 focus-visible:ring-gold-500"
                />
                {open && suggestions.length > 0 && (
                    <div
                        data-testid={`${testid}-suggestions`}
                        className="absolute z-20 mt-1.5 w-full rounded-md border border-navy-100 bg-white shadow-lift overflow-hidden max-h-64 overflow-y-auto"
                    >
                        {suggestions.map((s, i) => {
                            const text =
                                s.placePrediction?.text?.toString() || "";
                            return (
                                <button
                                    type="button"
                                    key={s.placePrediction?.placeId || i}
                                    data-testid={`${testid}-suggestion-${i}`}
                                    onMouseDown={(e) => {
                                        // mousedown (not click) so this fires
                                        // before the input's blur/outside-click
                                        // handlers close the dropdown first.
                                        e.preventDefault();
                                        selectSuggestion(s);
                                    }}
                                    onMouseEnter={() => setActiveIndex(i)}
                                    className={`w-full text-left px-3 py-2 text-sm text-navy-700 ${
                                        i === activeIndex ? "bg-gold-50" : "hover:bg-cream"
                                    }`}
                                >
                                    {text}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
            {isVerified && (
                <div
                    data-testid={`${testid}-verified`}
                    className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1 inline-flex items-center gap-1"
                >
                    <CheckCircle2 size={12} />
                    Address verified
                </div>
            )}
            <div className="text-[11px] text-navy-400">
                Powered by Google — start typing to search.
            </div>
        </div>
    );
}
